import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { PapiNetworkbarStrip } from '@wix/da-papi-types';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import BrowseThumbLayout from '../../../BrowseThumbLayout';
import Strips from '../../../Strips';
import ContentLoader from '../../../ContentLoader';
import EmptyResult from '../../../EmptyResult';
import BasePageLayout from '../../_partials/BasePageLayout';
import ShopPageHeader from './ShopPageHeader';
import SellersLandingPageLink from './SellersLandingPageLink';

import s from './ShopPage.scss';

export interface Props {
  searchTerm?: string;
  searchPageType?: string;
  totalResults?: number;
  initialOffset?: number;
  strips?: PapiNetworkbarStrip[];
  isInfiniteScroll?: boolean;
  isMobile?: boolean;
}

const SEARCHTYPE_CONVERTED_TO_SECTIONNAME = {
  downloads: 'Premium Downloads',
  commissions: 'Commissions',
  // TODO?: subscriptions: 'Subscriptions',
};

const ShopPage: React.FC<Props> = ({
  searchTerm,
  searchPageType = 'all',
  totalResults = 0,
  initialOffset,
  strips = [],
  isInfiniteScroll,
  isMobile,
}) => {
  const { t } = useTranslation();
  const shop_header_updates = useFlag('shop_header_updates');

  const isShowingTotalResults = !isMobile && !!searchTerm;
  const isShowingSellersLandingPageLink =
    searchPageType === 'all' || !isInfiniteScroll;

  let content: React.ReactNode | null = null;
  if (searchPageType === 'all') {
    content = strips?.length ? (
      <Strips
        className={s['all-tab-strips']}
        stripClassName={s['all-tab-strip']}
      />
    ) : (
      <EmptyResult />
    );
  } else {
    content = (
      <ContentLoader>
        <BrowseThumbLayout />
      </ContentLoader>
    );
  }

  return (
    <BasePageLayout
      allowSaleBanner={false}
      withNetworkBar={false}
      customHeader={
        <ShopPageHeader
          searchTerm={searchTerm}
          totalResults={totalResults}
          searchPageType={searchPageType}
        />
      }
      title={
        isShowingTotalResults && (
          <span className={s['results']}>
            {shop_header_updates
              ? t('shopPage.numResults.shop_header_updates', {
                  postProcess: 'reactPostprocessor',
                  formattedCount: shortenNumber(totalResults),
                  count: totalResults,
                  term: (
                    <span className={s['results-searchterm']}>
                      {searchTerm}
                    </span>
                  ),
                })
              : t('shopPage.numResults', {
                  formattedCount: shortenNumber(totalResults),
                  count: totalResults,
                })}
          </span>
        )
      }
    >
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>

      <BiLoggerContextProvider
        value={{
          sectionname: SEARCHTYPE_CONVERTED_TO_SECTIONNAME[searchPageType],
          _rankOffset: initialOffset,
        }}
      >
        {content}
        {isShowingSellersLandingPageLink && <SellersLandingPageLink />}
      </BiLoggerContextProvider>
    </BasePageLayout>
  );
};

ShopPage.displayName = 'ShopPage';
export default ShopPage;
